<template>
  <div
    ref="rootEl"
    :key="field.id"
    class="field-item"
    :class="{
      [`level-${field.level}`]: true,
      [`type-${field.type}`]: field.type,
      [`category-${category}`]: category,
      'has-metadata': hasMetadata,
      'is-collapsible': isCollapsible,
      'is-collapsed': isCollapsed,
      'is-ignored': isIgnored,
      'is-hidden-here': isHidden,
      'is-highlighted': isHighlighted,
      'is-nested': field.level > 0,
    }"
    :style="{
      '--level': field.level,
      '--color': field.color,
      '--bg': bgColor,
    }"
    :data-path="field.path.join('.')"
    @click="handleCollapse"
  >
    <div class="field-key">
      <div
        v-if="isCollapsible"
        class="field-square"
      >
        <IconButton
          class="field-expand"
          icon="fas fa-chevron-right"
          inherit
          @click.stop="handleCollapse"
        />
      </div>

      <div
        v-if="hasMetadata"
        class="field-square"
      >
        <ColorSwatch
          v-if="field.color"
          class="field-color"
          :color="field.color"
        />
      </div>

      <span class="field-id">
        {{ field.label }}
      </span>
    </div>

    <template
      v-if="!isCollapsible"
    >
      <div
        v-if="field.type === 'boolean'"
        class="stk-radios"
      >
        <label class="stk-radio">
          <input
            v-model="model"
            type="radio"
            :name="field.stringPath"
            :value="true"
          >
          Yes
        </label>

        <label class="stk-radio">
          <input
            v-model="model"
            type="radio"
            :name="field.stringPath"
            :value="false"
          >
          No
        </label>
      </div>

      <input
        v-else
        v-model="model"
        class="stk-reset field-value"
        type="text"
      >
    </template>

    <div class="field-controls">
      <div
        v-if="field.metadata?.confidence"
        class="field-confidence"
      >
        <ConfidenceLevel
          :confidence="field.metadata.confidence"
        />
      </div>

      <div
        v-if="hasMetadata"
        class="field-highlight"
        @mouseenter="handleMouseEnter"
        @mouseleave="handleMouseLeave"
      >
        <i class="fas fa-search" />
      </div>

      <IconButton
        class="field-toggle"
        inherit
        :icon="isIgnored ? 'fas fa-check' : 'fas fa-times'"
        :title="isIgnored ? 'Restore' : 'Mark for deletion'"
        @click="handleToggle"
      />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue';
import { ConfidenceLevel, IconButton } from '@stakeui';
import ColorSwatch from './ColorSwatch.vue';
import { useInspectorStore } from '@stores/documentInspectorStore.js';

const inspectorStore = useInspectorStore();

const rootEl = ref(null);

const props = defineProps({
  field: {
    type: Object,
    required: true,
  },
});

const category = computed(() => {
  if (['object', 'array'].includes(props.field.type)) {
    return props.field.type;
  }

  return 'primitive';
});

const model = computed({
  get: () => props.field.value,
  set: v => inspectorStore.setFieldValue(props.field.stringPath, v),
});

const isCollapsible = computed(() => ['object', 'array'].includes(category.value));
const hasMetadata = computed(() => props.field.metadata?.bbox);
const isCollapsed = computed(() => inspectorStore.collapsedSet.has(props.field.stringPath));
const isIgnored = computed(() => inspectorStore.ignoredFields.has(props.field.stringPath));
const isHighlighted = computed(() => inspectorStore.clickedZones.has(props.field.stringPath));
const isHidden = computed(() => {
  // Iterate through collapsedSet and hide if current path starts with it + '.'
  for (const collapsedKey of inspectorStore.collapsedSet) {
    if (props.field.stringPath.startsWith(`${collapsedKey}.`)) {
      return true;
    }
  }

  return false; // either unrelated or the exact node
});
const bgColor = computed(() => `${props.field.color}33`);

const handleCollapse = () => {
  if (!isCollapsible.value) {
    return;
  }

  inspectorStore.toggleCollapse(props.field.stringPath);
};

const handleMouseEnter = () => {
  inspectorStore.hoverEnter(props.field.stringPath);
};

const handleMouseLeave = () => {
  inspectorStore.hoverLeave(props.field.stringPath);
};

const handleToggle = () => {
  inspectorStore.toggleIgnore(props.field.stringPath);
};

watch(isHighlighted, newVal => {
  if (newVal && rootEl.value) {
    rootEl.value.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }
});
</script>

<style scoped>
.field-item {
  --controls-width: 9em;
  align-items: center;
  border-radius: var(--radius);
  display: grid;
  gap: var(--spacing);
  grid-template-columns: calc(12em - calc(var(--level) * var(--offset))) 1fr var(--controls-width);
  margin-left: calc(var(--offset) * var(--level));
  padding: var(--spacing);
  position: relative;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color, box-shadow;
  transform-origin: center;

  &.is-collapsible {
    grid-template-columns: 1fr var(--controls-width);

    &:hover {
      background-color: var(--stk-text-90);
    }

    &:active {
      background-color: var(--stk-text-85);
    }
  }

  &.is-collapsed {
    .field-expand {
      transform: rotate(0);
    }
  }

  &.is-hidden-here {
    display: none;
  }

  &.category-primitive {
    background-color: #fff;
    box-shadow: var(--stk-control-shadow);

    &:hover {
      box-shadow: 0 0 0 1px var(--stk-primary), 0 0 0.25em var(--stk-primary);
      z-index: 1;
    }

    &.is-ignored {
      background-color: transparent;
      box-shadow: none;
      color: var(--stk-text-weak);

      .field-value {
        color: inherit;
      }
    }
  }

  &.has-metadata {
    .field-highlight,
    .field-color {
      opacity: 1;
    }
  }

  &.is-highlighted {
    animation-name: stk-pop-in;
    animation-duration: 400ms;
    animation-timing-function: linear;
    z-index: 2;
  }
}

.field-key {
  display: flex;
  align-items: center;
  gap: var(--spacing);
}

.field-highlight,
.field-confidence,
.field-square {
  display: flex;
  height: 1.25em;
  width: 1.25em;
  align-items: center;
  justify-content: center;
}

.field-confidence {
  font-size: 0.75em;
  width: 2em;
  justify-content: end;
  margin-right: var(--spacing);
}

.field-expand {
  color: var(--stk-text-70);
  transform-origin: center;
  transition-property: transform;
  transition-duration: var(--stk-transition-duration);
  transform: rotate(90deg);
  font-size: 0.75em;
}

.field-id {
  font-size: 0.875rem;
  font-weight: 500;
  line-height: 1.25rem;
}

.field-value {
  border: none;
  color: var(--stk-text-strong);
  line-height: 1rem;
  padding: 0.25rem 0.375rem;
  margin: calc(-0.125rem - 3px) 0;

  &:hover {
    background-color: var(--stk-background);
    box-shadow: 0 0 0 1px var(--stk-border);
  }

  &:focus-visible {
    background-color: #fff;
    outline-color: var(--stk-control-focus-outline-color);
    outline-style: var(--stk-control-focus-outline-style);
    outline-width: 3px;
    box-shadow: 0 0 0 1px var(--stk-primary);
  }
}

.field-controls {
  display: flex;
  align-items: center;
  justify-content: end;
}

.field-highlight {
  /* color: var(--color); */
  color: var(--stk-primary);
  font-size: 0.75em;
  flex-shrink: 0;
  height: calc(2rem + 2px);
  width: calc(2rem + 2px);
  margin: -0.75rem 0;
  margin-left: 0;
  transition-duration: var(--stk-transition-duration);
  transition-property: background-color;

  &:hover {
    background-color: hsl(var(--stk-primary-h) var(--stk-primary-s) var(--stk-primary-l) / 20%);
  }
}

.field-toggle {
  color: var(--stk-text-weak);
  font-size: 0.75em;
  transition-duration: var(--stk-transition-duration);
  transition-property: opacity;
  opacity: 0.5;

  &:hover {
    opacity: 1;
  }
}

.field-highlight,
.field-color {
  opacity: 0;
}

@keyframes stk-pop-in {
  0% {
    transform: scale(1);
  }

  50% {
    background-color: var(--stk-primary-80);
    transform: scale(1.05);
  }

  100% {
    transform: scale(1);
  }
}
</style>
